// Hook exposing auth state + actions to React components.

import { useCallback } from 'react';
import { useAuthStore } from './auth.store';
import * as authApi from './auth.api';

export function useAuth() {
  const user = useAuthStore((s) => s.user);
  const accessToken = useAuthStore((s) => s.accessToken);
  const isHydrated = useAuthStore((s) => s.isHydrated);
  const setAuth = useAuthStore((s) => s.setAuth);
  const setUser = useAuthStore((s) => s.setUser);
  const setHydrated = useAuthStore((s) => s.setHydrated);
  const clear = useAuthStore((s) => s.clear);

  const login = useCallback(
    async (input: authApi.LoginInput) => {
      const res = await authApi.login(input);
      setAuth(res.user, res.accessToken);
      return res;
    },
    [setAuth],
  );

  const logout = useCallback(async () => {
    try {
      await authApi.logout();
    } finally {
      clear();
    }
  }, [clear]);

  /** On boot, attempt /me to recover a session if the refresh cookie is valid. */
  const hydrate = useCallback(async () => {
    try {
      const me = await authApi.fetchMe();
      setUser(me);
    } catch {
      clear();
    } finally {
      setHydrated(true);
    }
  }, [setUser, clear, setHydrated]);

  return {
    user,
    accessToken,
    isAuthenticated: !!user,
    isHydrated,
    login,
    logout,
    hydrate,
  };
}
